import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ClosingReasonSchema, isReasonCode, type TimelineEntry } from '../../api';
import { useLanguage } from '../../app/i18n';
import { NUMERIC_FONT } from '../../app/theme';
import { reasonMessage } from '../../i18n';
import { formatClockTime } from '../shared/format-time';

import { type ExecutionEntry, type TimelineStep, toTimelineSteps } from './timeline-model';
import { describeInterpretation } from './understood';
import { usePositionTimeline } from './use-positions';

/** The reply arrow of mockup 7 ("↳ in reply"); decorative, hidden from screen readers. */
const REPLY_ARROW = '↳';

/** Sizes in px drawn on mockup 7 (`docs/06-MiniApp/Maquettes/Positions.html`), timeline part. */
const MOCKUP7 = {
  /** Width of the time column ("09:14"). */
  timeColumn: '44px',
  /** Step text. */
  bodySize: 13,
  /** "Message corrected by the channel" label, as the mockup's small labels. */
  labelSize: 12,
  /** Corner of the quoted channel message. */
  quoteRadius: '10px',
  /** Skeleton of one step while the timeline loads. */
  stepSkeletonHeight: 48,
} as const;

/**
 * What the channel said and what FXMate understood and did, for one position (spec R6, I10;
 * mockup 7 "What the channel said, what FXMate did"). Fetches `GET /positions/{id}/timeline` once
 * when mounted: the card mounts it on expand. Works for open (M5) and closed (M6) positions.
 */
export function PositionTimeline({ positionId }: { readonly positionId: string }): ReactElement {
  const { t } = useTranslation();
  const timeline = usePositionTimeline(positionId);
  if (timeline.status === 'pending') {
    return (
      <Box
        role="status"
        aria-label={t('positions.timeline.loading')}
        sx={{ display: 'grid', gap: 1 }}
      >
        <Skeleton variant="rounded" height={MOCKUP7.stepSkeletonHeight} />
        <Skeleton variant="text" width="70%" />
      </Box>
    );
  }
  if (timeline.status === 'error') {
    const message = reasonMessage(timeline.error.reasonCode, timeline.error.params);
    return (
      <Alert
        severity="error"
        sx={(theme) => ({ bgcolor: theme.fx.dangerBg, color: theme.fx.text })}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={timeline.refetch}
            loading={timeline.retrying}
            sx={{ minHeight: 36 }}
          >
            {t('positions.retry')}
          </Button>
        }
      >
        {t(message.key, message.values)}
      </Alert>
    );
  }
  if (!timeline.data.available) {
    // C1 decision 7: the message → position link is not known (Q34). Said, never hidden (I10).
    return <Unavailable />;
  }
  return <TimelineSteps entries={timeline.data.entries} />;
}

/** The `TIMELINE_UNAVAILABLE` text of C2 (spec §4 "Timeline unavailable"). */
function Unavailable(): ReactElement {
  const { t } = useTranslation();
  return (
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {t('reason.TIMELINE_UNAVAILABLE')}
    </Typography>
  );
}

/** The steps of an available timeline, or the "not available" text when it has none (spec §4). */
function TimelineSteps({ entries }: { readonly entries: readonly TimelineEntry[] }): ReactElement {
  const steps = toTimelineSteps(entries);
  if (steps.length === 0) {
    // Provisional: `available: true` with no entry has no copy of its own, so it reads as
    // "not available" too (nothing to show is never an empty box).
    return <Unavailable />;
  }
  return (
    <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
      {steps.map((step) => (
        <Step key={step.key} step={step} />
      ))}
    </Box>
  );
}

function Step({ step }: { readonly step: TimelineStep }): ReactElement {
  const { t } = useTranslation();
  const language = useLanguage();
  const at = step.message?.at ?? step.interpretation?.at ?? step.executions[0]?.at;
  return (
    <Box
      component="li"
      sx={{
        display: 'grid',
        gridTemplateColumns: `${MOCKUP7.timeColumn} minmax(0, 1fr)`,
        gap: 1,
        fontSize: MOCKUP7.bodySize,
      }}
    >
      <Box component="span" sx={{ fontFamily: NUMERIC_FONT, color: 'text.secondary' }}>
        {at === undefined ? null : <time dateTime={at}>{formatClockTime(at, language)}</time>}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
        {step.markers.includes('edited') && (
          // An edit by the channel: each revision is its own step, labelled (spec §4, AC15, I10).
          <Box sx={{ color: 'text.secondary', fontSize: MOCKUP7.labelSize, fontWeight: 600 }}>
            {t('positions.timeline.edited')}
          </Box>
        )}
        {step.message !== null && (
          // Verbatim: never translated or rewritten (spec R6). `lang` is not set: the channel's
          // language is unknown, and the UI language would be wrong for it.
          <Box
            component="blockquote"
            sx={(theme) => ({
              m: 0,
              bgcolor: theme.fx.page,
              borderRadius: MOCKUP7.quoteRadius,
              px: 1.25,
              py: 1,
              whiteSpace: 'pre-line',
              overflowWrap: 'anywhere',
              lineHeight: 1.4,
            })}
          >
            {step.markers.includes('reply') && (
              // Mockup 7: "↳ in reply: securing it, SL to BE" (C1 `replyToMessageId`).
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                <span aria-hidden>{REPLY_ARROW} </span>
                {t('positions.timeline.reply')}{' '}
              </Box>
            )}
            <span>{step.message.text}</span>
          </Box>
        )}
        {step.markers.includes('deleted') && (
          // FXM-03 §4: a deleted opening message does not close the position. The text stays
          // above, verbatim, so the trade can still be reconstructed (I10).
          <Box sx={{ color: 'text.secondary', fontSize: MOCKUP7.labelSize, fontWeight: 600 }}>
            {t('positions.timeline.deleted')}
          </Box>
        )}
        {step.interpretation !== null && (
          <Box sx={{ lineHeight: 1.4 }}>
            <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
              {t('positions.timeline.understood')}
            </Box>{' '}
            {describeInterpretation(step.interpretation, t, language)}
          </Box>
        )}
        {step.executions.map((execution, index) => (
          <Execution key={`${execution.at}-${String(index)}`} execution={execution} />
        ))}
      </Box>
    </Box>
  );
}

/** What was executed; a failure shows only the C2 text of its reason code (spec R8, I12). */
function Execution({ execution }: { readonly execution: ExecutionEntry }): ReactElement {
  const { t } = useTranslation();
  if (execution.outcome === 'UNKNOWN') {
    // An outcome added by a newer API (C1 R10): a neutral line, never the raw value.
    return <Box sx={{ color: 'text.secondary' }}>{t('positions.timeline.outcomeUnknown')}</Box>;
  }
  if (execution.outcome === 'PENDING') {
    // Awaiting the trader's validation (C1 §5.2, provisional Q25): said, with no action here.
    return <Box sx={{ color: 'text.secondary' }}>{t('positions.timeline.pending')}</Box>;
  }
  if (execution.outcome === 'EXECUTED') {
    // A close FXMate made on the trader's account says who asked for it (M5 R26): "Executed ·
    // Closed by the emergency stop". Any other code on an executed line is not a closing reason
    // and is ignored (C2 R7: no raw value, no generic fallback for a success).
    const closing = ClosingReasonSchema.safeParse(execution.reasonCode);
    const label = closing.success ? reasonMessage(closing.data) : null;
    return (
      <Box sx={(theme) => ({ color: theme.fx.success, fontWeight: 500 })}>
        {t('positions.timeline.executed')}
        {label !== null && (
          <>
            {' · '}
            {t(label.key, label.values)}
          </>
        )}
      </Box>
    );
  }
  if (execution.outcome === 'NOT_APPLICABLE' && !isReasonCode(execution.reasonCode)) {
    // No reason, or one this version does not know: the generic C2 fallback says "try again in a
    // moment", wrong for a rule that stopped a past instruction, so a neutral line instead
    // (M5 §8 "Fallback text", proposal).
    return <Box sx={{ color: 'text.secondary' }}>{t('positions.timeline.notAppliedNoReason')}</Box>;
  }
  // A missing or unknown code falls back to the generic C2 text (C2 R7), never the raw value.
  const message = reasonMessage(execution.reasonCode ?? undefined);
  // NOT_APPLICABLE: nothing went wrong, a rule stopped it (paused channel, daily limit, no SL;
  // C1 §5.2), so it reads in the neutral colour, not as a failure.
  const notApplied = execution.outcome === 'NOT_APPLICABLE';
  return (
    <Box
      sx={(theme) => ({
        color: notApplied ? theme.palette.text.secondary : theme.fx.danger,
        lineHeight: 1.4,
      })}
    >
      <Box component="span" sx={{ fontWeight: 600 }}>
        {t(notApplied ? 'positions.timeline.notApplied' : 'positions.timeline.failed')}
      </Box>{' '}
      {t(message.key, message.values)}
    </Box>
  );
}
